import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import type { Standing } from '@brochure/shared'
import { cn } from '@/lib/utils'

type LiveScore = { myScore: number; oppScore: number }
type StandingsTableProps = {
  standings: Standing[]
  advancing: number
  /** Girone concluso (o assente): solo allora chi passa e chi è fuori è definitivo. */
  isGroupOver: boolean
  liveByTeam: Map<string, LiveScore>
}

// Colonne secondarie (vinte, perse, bicchieri fatti/subiti): da `sm` in su. A 375px restano #, squadra, PT, G, diff.
const WIDE = 'hidden sm:table-cell'

/**
 * Classifica del girone senza scorrimento orizzontale sul telefono. A girone concluso le prime `advancing` righe
 * sono qualificate (numero su fondo mare, il primo su fondo sole) e le altre "OUT"; durante il girone solo una
 * linea tratteggiata segna la zona eliminatorie. Chip del punteggio per le squadre in campo ora.
 */
export function StandingsTable({ standings, advancing, isGroupOver, liveByTeam }: StandingsTableProps) {
  const { t } = useTranslation()
  const head = 'px-2 py-2.5 text-center font-semibold'
  return (
    <div className="overflow-hidden rounded-xl border border-ink/10 bg-paper shadow-card">
      <table className="w-full font-body text-sm">
        <thead>
          <tr className="border-b border-sun/45 bg-paper-deep/60 text-left text-xs tracking-[0.02em] text-ink-soft">
            <th scope="col" className="w-10 py-2.5 pl-3 font-semibold">
              #
            </th>
            <th scope="col" className="px-2 py-2.5 font-semibold">
              {t('zambePong.standings.team')}
            </th>
            <th scope="col" className={head}>
              {t('zambePong.standings.points')}
            </th>
            <th scope="col" className={head}>
              {t('zambePong.standings.played')}
            </th>
            <th scope="col" className={cn(head, WIDE)}>
              {t('zambePong.standings.wins')}
            </th>
            <th scope="col" className={cn(head, WIDE)}>
              {t('zambePong.standings.losses')}
            </th>
            <th scope="col" className={cn(head, WIDE)}>
              {t('zambePong.standings.cupsFor')}
            </th>
            <th scope="col" className={cn(head, WIDE)}>
              {t('zambePong.standings.cupsAgainst')}
            </th>
            <th scope="col" className={cn(head, 'pr-3')}>
              {t('zambePong.standings.diff')}
            </th>
          </tr>
        </thead>
        <tbody>
          {standings.map((standing, index) => {
            const qualified = !isGroupOver || index < advancing
            const medal = isGroupOver && index < advancing
            const cutLine = !isGroupOver && index === advancing - 1 && index < standings.length - 1
            const live = liveByTeam.get(standing.team.id)
            const liveTone = live ? (live.myScore > live.oppScore ? 'bg-sea/15 text-sea-ink' : live.myScore < live.oppScore ? 'bg-coral/15 text-coral-ink' : 'bg-ink/5 text-ink-soft') : ''
            return (
              <Fragment key={standing.team.id}>
              <tr
                data-testid={`standing-${standing.team.id}`}
                className={cn('border-b border-line/70 last:border-0', cutLine && 'border-b-0', qualified ? 'qualified' : 'eliminated bg-ink/[0.03]')}
              >
                <td className="py-2.5 pl-3">
                  <span
                    className={cn(
                      'grid size-6 place-items-center rounded-full text-xs font-bold tabular-nums',
                      index === 0 && medal ? 'bg-sun text-ink' : medal ? 'bg-sea/20 text-sea-ink' : 'text-ink-soft',
                    )}
                  >
                    {index + 1}
                  </span>
                </td>
                <td className="px-2 py-2.5">
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    <span className={cn('font-medium [overflow-wrap:anywhere]', qualified ? 'text-ink' : 'text-ink-soft')}>{standing.team.name}</span>
                    {!qualified && <span className="shrink-0 text-xs font-bold tracking-[0.02em]st text-coral-ink">{t('zambePong.standings.out')}</span>}
                    {live && <span className={cn('shrink-0 rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums', liveTone)}>{live.myScore}-{live.oppScore}</span>}
                  </div>
                </td>
                <td className="px-2 py-2.5 text-center font-body text-base tabular-nums text-ink">{standing.points}</td>
                <td className="px-2 py-2.5 text-center tabular-nums text-ink-soft">{standing.played}</td>
                <td className={cn('px-2 py-2.5 text-center tabular-nums text-ink-soft', WIDE)}>{standing.wins}</td>
                <td className={cn('px-2 py-2.5 text-center tabular-nums text-ink-soft', WIDE)}>{standing.losses}</td>
                <td className={cn('px-2 py-2.5 text-center tabular-nums text-ink-soft', WIDE)}>{standing.goalsFor}</td>
                <td className={cn('px-2 py-2.5 text-center tabular-nums text-ink-soft', WIDE)}>{standing.goalsAgainst}</td>
                <td className="px-2 py-2.5 pr-3 text-center tabular-nums text-ink-soft">{standing.goalDiff > 0 ? `+${standing.goalDiff}` : standing.goalDiff}</td>
              </tr>
              {cutLine && (
                // Riga alta zero: la linea si appoggia sul confine tra le due righe senza allungare la tabella.
                <tr data-testid="standings-cut-line">
                  <td colSpan={9} className="h-0 p-0">
                    <div className="relative">
                      <div className="absolute inset-x-3 top-0 flex -translate-y-1/2 items-center gap-2 text-xs font-semibold leading-none tracking-[0.02em]st text-sea-ink/45">
                        <span className="flex-1 border-t border-sun/50" aria-hidden="true" />
                        {t('zambePong.standings.cutLine')}
                        <span className="flex-1 border-t border-sun/50" aria-hidden="true" />
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
